import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useMemo } from 'react';
import { buildFilteredHighlightedProducts, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { isBundleListWithConsistentTypeSelector } from '../../../selectors/ProductsSelectors';
import { sortApiNamesByProductLevel } from '../../../utils/sortingUtils';
import { CustomError } from '../../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../../v2/components/error-boundary/withErrorBoundary';
import { productLineToSkuCategoryName, skuCategoryNameToProductLine } from '../../../v2/constants';
import { useBadges } from '../../../v2/hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../../v2/hooks/features/useFeatures';
import ProductIcon from '../../common/ProductIcon';
import GenericBundleFeatureTableHeaderCell from './GenericBundleFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LabelContent = ({
  skuCategoryName
}) => {
  const productLine = skuCategoryNameToProductLine[skuCategoryName];
  if (!productLine) return null;
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(ProductIcon, {
      name: productLine,
      iconStyle: {
        inlineSize: '24px',
        marginInlineEnd: '300'
      }
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-300",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "suiteFeatureTable.section.title",
        options: {
          product: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `productLine.${productLine}`
          })
        }
      })
    })]
  });
};
const buildFeatureTableHeaderData = (apiNames, screen, trackingSource, badges) => {
  const sortedApiNames = sortApiNamesByProductLevel(apiNames);
  return apiNames.reduce((headerDataMap, apiName, index) => {
    var _badges$apiName;
    headerDataMap[apiName] = {
      Content: /*#__PURE__*/_jsx(GenericBundleFeatureTableHeaderCell, {
        apiName: sortedApiNames[index],
        screen: screen,
        trackingSource: trackingSource,
        badgeName: badges === null || badges === void 0 || (_badges$apiName = badges[apiName]) === null || _badges$apiName === void 0 ? void 0 : _badges$apiName.badgeName
      }, apiName)
    };
    return headerDataMap;
  }, {});
};
const buildFeatureTableBodyData = (features, productLevelsFromBundle, skuCategoryNamesFromBundle, screen) => {
  const sectionData = skuCategoryNamesFromBundle.reduce((sectionDataMap, skuCategoryName) => {
    const productLine = skuCategoryNameToProductLine[skuCategoryName];
    sectionDataMap[skuCategoryName] = {
      features: features[skuCategoryName] || [],
      LabelContent: /*#__PURE__*/_jsx(LabelContent, {
        skuCategoryName: skuCategoryName
      }),
      apiNames: productLine ? buildFilteredHighlightedProducts({
        productLine,
        productLevels: productLevelsFromBundle
      }) : []
    };
    return sectionDataMap;
  }, {});
  if (Object.keys(sectionData).length > 0) {
    return {
      sharedData: {
        subscreen: screen
      },
      sectionData
    };
  }
};
const GenericBundleFeatureTable = ({
  apiNames,
  productLinesFromBundle,
  screen,
  trackingSource
}) => {
  const {
    showBoundary
  } = useErrorBoundary();
  const {
    badges
  } = useBadges({
    productApiNames: apiNames
  });
  const isSeatsRedesignUngated = useAppSelector(isUngatedForSeatsSelector);
  const productLevelsFromBundle = useMemo(() => apiNames.map(apiName => {
    const level = getProductLevelFromApiNameString(apiName);
    return level;
  }), [apiNames]);
  if (!productLevelsFromBundle) {
    showBoundary(new CustomError('Invalid product lines', {
      productLinesFromBundle,
      trackingSource
    }));
  }
  const skuCategoryNameToApiName = productLinesFromBundle.reduce((acc, productLine) => {
    acc[productLineToSkuCategoryName[productLine]] = buildFilteredHighlightedProducts({
      productLine,
      productLevels: productLevelsFromBundle
    });
    return acc;
  }, {});
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = usePinnedFeaturesBySkuCategoryName(skuCategoryNameToApiName);
  const isBundleWithConsistentType = useAppSelector(state => {
    return isBundleListWithConsistentTypeSelector(state, {
      apiNames
    });
  });
  if (!isBundleWithConsistentType) {
    showBoundary(new CustomError('Bundle type is not consistent', {
      isBundleWithConsistentType,
      trackingSource
    }));
  }
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError(error.message || 'Error fetching features', {
      error,
      productLevelsFromBundle,
      apiNames,
      skuCategoryNameToApiName,
      trackingSource,
      fingerprint: ['errorFetchingFeatures']
    }));
  }
  if (!featuresBySkuCategoryName || !Object.keys(featuresBySkuCategoryName).length) {
    return null;
  }
  const headerData = buildFeatureTableHeaderData(apiNames, screen, trackingSource, badges);
  const skuCategoryNamesFromBundle = productLinesFromBundle.map(productLine => productLineToSkuCategoryName[productLine]);
  const bodyData = buildFeatureTableBodyData(featuresBySkuCategoryName, productLevelsFromBundle, skuCategoryNamesFromBundle, screen);
  if (!bodyData) {
    return null;
  }
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: isSeatsRedesignUngated,
    isInbound25PricingPage: true
  });
};
export default withErrorBoundary(GenericBundleFeatureTable, {
  fallback: null,
  errorBoundaryName: 'GENERIC_BUNDLE_FEATURE_TABLE'
});